import { FormEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import SortableTableHeader from './components/SortableTableHeader';
import { showToastError, showToastSuccess } from './components/ToastNotifications';
import { useSsoReauth } from './contexts/SsoReauthContext';
import { getStagedPackageSummary, promoteStagedPackage } from './hooks/usePackages';
import { isSsoReauthRequired, isSsoTemporarilyUnavailable } from './utils/requestErrors';
import { getNextSortCriterion, type FieldSortCriterionValue } from './utils/tableSort';

type PromotionFacility = {
  id: number;
  name: string;
  license: string;
};

type PromotionCandidate = {
  key: string;
  organizationId: number;
  organizationName: string;
  stagedLabel: string;
  facilities: PromotionFacility[];
  stagedRecordCount: number;
};

type PromotionOutcome = {
  status: 'success' | 'error';
  message: string;
};

const readApiError = (error: any, fallback: string): string => {
  const body = error?.body ?? error?.respBody;
  if (typeof body === 'string' && body.trim()) return body.trim();
  if (typeof body?.error === 'string' && body.error.trim()) return body.error.trim();
  if (typeof body?.message === 'string' && body.message.trim()) return body.message.trim();
  if (typeof error?.message === 'string' && error.message.trim()) return error.message.trim();
  return fallback;
};

const isApiErrorResponse = (response: any): boolean =>
  response?.error === true || Number(response?.status ?? 0) >= 400;

const normalizePromotionCandidates = (response: unknown): PromotionCandidate[] => {
  const summaries = Array.isArray(response) ? response : [];
  const candidates = new Map<string, PromotionCandidate>();

  summaries.forEach((summary: any) => {
    const stagedPackage = summary?.stagedPackage ?? summary?.StagedPackage ?? {};
    const organization = summary?.organization ?? summary?.Organization ?? {};
    const facility = summary?.facility ?? summary?.Facility ?? {};
    const organizationId = Number(organization?.id ?? organization?.Id ?? 0);
    const stagedLabel = String(stagedPackage?.packageLabel ?? stagedPackage?.PackageLabel ?? '').trim();

    if (organizationId <= 0 || !stagedLabel) return;

    const key = `${organizationId}:${stagedLabel.toLowerCase()}`;
    const facilityId = Number(facility?.id ?? facility?.Id ?? stagedPackage?.facilityId ?? stagedPackage?.FacilityId ?? 0);
    const normalizedFacility: PromotionFacility = {
      id: facilityId,
      name: String(facility?.name ?? facility?.Name ?? ''),
      license: String(facility?.license ?? facility?.License ?? ''),
    };
    const existing = candidates.get(key);

    if (existing) {
      existing.stagedRecordCount += 1;
      if (facilityId > 0 && !existing.facilities.some((item) => item.id === facilityId)) {
        existing.facilities.push(normalizedFacility);
      }
      return;
    }

    candidates.set(key, {
      key,
      organizationId,
      organizationName: String(organization?.name ?? organization?.Name ?? ''),
      stagedLabel,
      facilities: facilityId > 0 ? [normalizedFacility] : [],
      stagedRecordCount: 1,
    });
  });

  return Array.from(candidates.values()).sort((left, right) => {
    const organizationComparison = left.organizationName.localeCompare(right.organizationName);
    if (organizationComparison !== 0) return organizationComparison;
    return left.organizationId - right.organizationId;
  });
};

export default function StagedPackagePromotionPage() {
  const navigate = useNavigate();
  const { handleSsoError } = useSsoReauth();
  const [label, setLabel] = useState('');
  const [candidates, setCandidates] = useState<PromotionCandidate[]>([]);
  const [sort, setSort] = useState<FieldSortCriterionValue>({
    fieldName: 'organizationName',
    isAscending: true,
  });
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [outcomes, setOutcomes] = useState<Record<string, PromotionOutcome>>({});
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);

  const selectedCandidates = useMemo(
    () => candidates.filter((candidate) => selectedKeys.includes(candidate.key)),
    [candidates, selectedKeys],
  );

  const sortedCandidates = useMemo(() => {
    const getSortValue = (candidate: PromotionCandidate): string | number => {
      if (sort.fieldName === 'stagedLabel') return candidate.stagedLabel;
      if (sort.fieldName === 'organizationName') {
        return candidate.organizationName || candidate.organizationId;
      }
      if (sort.fieldName === 'facility') {
        return candidate.facilities
          .map((facility) => `${facility.name} ${facility.license}`.trim())
          .join(', ');
      }
      if (sort.fieldName === 'stagedRecordCount') return candidate.stagedRecordCount;
      if (sort.fieldName === 'status') return outcomes[candidate.key]?.message || 'Ready';
      return '';
    };

    return [...candidates].sort((left, right) => {
      const leftValue = getSortValue(left);
      const rightValue = getSortValue(right);
      const comparison = typeof leftValue === 'number' && typeof rightValue === 'number'
        ? leftValue - rightValue
        : String(leftValue).localeCompare(String(rightValue), undefined, { sensitivity: 'base' });

      if (comparison !== 0) return sort.isAscending ? comparison : -comparison;
      return left.key.localeCompare(right.key);
    });
  }, [candidates, outcomes, sort.fieldName, sort.isAscending]);

  const toggleSortDirection = (fieldName: string) => {
    setSort((current) => getNextSortCriterion(current, fieldName));
  };

  const searchStagedPackages = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedLabel = label?.trim() || '';

    if (!trimmedLabel) {
      showToastError('Package label is required.');
      return;
    }

    setIsSearching(true);
    setCandidates([]);
    setSelectedKeys([]);
    setOutcomes({});
    setHasSearched(false);

    try {
      const response = await getStagedPackageSummary(trimmedLabel);
      if (isApiErrorResponse(response)) throw response;

      const normalized = normalizePromotionCandidates(response);
      setCandidates(normalized);
      setHasSearched(true);

      if (!normalized.length) {
        showToastError('No staged packages were found for that label.');
      }
    } catch (error) {
      setHasSearched(true);
      showToastError(readApiError(error, 'Could not search staged packages.'));
    } finally {
      setIsSearching(false);
    }
  };

  const toggleCandidate = (candidate: PromotionCandidate) => {
    if (isPromoting || outcomes[candidate.key]?.status === 'success') return;

    setSelectedKeys((current) => current.includes(candidate.key)
      ? current.filter((key) => key !== candidate.key)
      : [...current, candidate.key]);
  };

  const promoteSelected = async () => {
    if (!selectedCandidates.length) {
      showToastError('Select at least one staged package.');
      return;
    }

    const confirmed = window.confirm(
      `Promote ${selectedCandidates.length} selected staged package${selectedCandidates.length === 1 ? '' : 's'}?`,
    );
    if (!confirmed) return;

    setIsPromoting(true);

    const promotionResults = await Promise.all(selectedCandidates.map(async (candidate) => {
      try {
        const response = await promoteStagedPackage({
          organizationId: candidate.organizationId,
          stagedLabel: candidate.stagedLabel,
        });
        if (isApiErrorResponse(response)) throw response;

        return {
          key: candidate.key,
          outcome: {status: 'success', message: 'Promoted'} as PromotionOutcome,
        };
      } catch (error) {
        return {
          key: candidate.key,
          error,
          outcome: {
            status: 'error',
            message: readApiError(error, 'Promotion failed.'),
          } as PromotionOutcome,
        };
      }
    }));

    const nextOutcomes = promotionResults.reduce<Record<string, PromotionOutcome>>((result, item) => {
      result[item.key] = item.outcome;
      return result;
    }, {});
    const successCount = promotionResults.filter((item) => item.outcome.status === 'success').length;
    const failureCount = promotionResults.length - successCount;
    // SSO organizations only: failures caused by no working Metrc sign-in share one prompt.
    const ssoError = promotionResults
      .map((item) => ('error' in item ? item.error : undefined))
      .find((error) => isSsoReauthRequired(error) || isSsoTemporarilyUnavailable(error));

    setOutcomes((current) => ({...current, ...nextOutcomes}));
    setSelectedKeys((current) => current.filter((key) => nextOutcomes[key]?.status !== 'success'));
    setIsPromoting(false);

    if (successCount > 0) {
      showToastSuccess(`${successCount} staged package${successCount === 1 ? '' : 's'} promoted.`);
    }
    if (failureCount > 0 && !(ssoError && handleSsoError(ssoError))) {
      showToastError(`${failureCount} staged package promotion${failureCount === 1 ? '' : 's'} failed.`);
    }
  };

  return (
    <div className="stack" data-testid="page-staged-package-promotion">
      <PageHeader
        title="Promote Staged Packages"
        subtitle="Search by package label, select the correct organization, and promote"
        actions={
          <button type="button" className="button" onClick={() => navigate('/internal')} data-testid="staged-package-promotion-back">
            Back
          </button>
        }
      />

      <form className="card filter-card" onSubmit={searchStagedPackages}>
        <div className="filter-bar">
          <div className="filter-left">
            <input
              className="input filter-search"
              type="search"
              placeholder="Enter the complete staged package label"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              disabled={isSearching || isPromoting}
              data-testid="staged-package-promotion-search"
            />
          </div>
          <button
            className="button cta"
            type="submit"
            disabled={isSearching || isPromoting || !label.trim()}
            data-testid="staged-package-promotion-search-submit"
          >
            {isSearching ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      <div className="card" data-testid="staged-package-promotion-results">
        <table className="table" data-testid="staged-package-promotion-table">
          <thead>
            <tr>
              <th aria-label="select package" />
              <SortableTableHeader label="Package label" fieldName="stagedLabel" sort={sort} onSort={toggleSortDirection} />
              <SortableTableHeader label="Organization" fieldName="organizationName" sort={sort} onSort={toggleSortDirection} />
              <SortableTableHeader label="Facility / license" fieldName="facility" sort={sort} onSort={toggleSortDirection} />
              <SortableTableHeader label="Staged records" fieldName="stagedRecordCount" sort={sort} onSort={toggleSortDirection} />
              <SortableTableHeader label="Status" fieldName="status" sort={sort} onSort={toggleSortDirection} />
            </tr>
          </thead>
          <tbody>
            {sortedCandidates.map((candidate) => {
              const outcome = outcomes[candidate.key];
              const isSelected = selectedKeys.includes(candidate.key);
              const isComplete = outcome?.status === 'success';

              return (
                <tr key={candidate.key} data-testid={`staged-package-promotion-row-${candidate.key}`} data-selected={isSelected}>
                  <td>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isPromoting || isComplete}
                      onChange={() => toggleCandidate(candidate)}
                      aria-label={`Select ${candidate.stagedLabel} for organization ${candidate.organizationId}`}
                      data-testid={`staged-package-promotion-select-${candidate.key}`}
                    />
                  </td>
                  <td><code>{candidate.stagedLabel}</code></td>
                  <td>
                    <div>{candidate.organizationName || 'Unnamed organization'}</div>
                    <div className="muted">ID: {candidate.organizationId}</div>
                  </td>
                  <td>
                    {candidate.facilities.length > 0
                      ? candidate.facilities.map((facility) => (
                          <div key={facility.id}>
                            {facility.name || 'Unnamed facility'}
                            {' — '}
                            {facility.license || 'No license'}
                            <span className="muted"> (ID: {facility.id})</span>
                          </div>
                        ))
                      : '-'}
                  </td>
                  <td>{candidate.stagedRecordCount}</td>
                  <td style={{color: outcome?.status === 'error' ? 'var(--error, red)' : undefined}}>
                    {outcome?.message || 'Ready'}
                  </td>
                </tr>
              );
            })}
            {hasSearched && candidates.length === 0 && (
              <tr data-testid="staged-package-promotion-empty">
                <td colSpan={6}>No staged packages found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <button
        className="button cta"
        type="button"
        disabled={!selectedCandidates.length || isSearching || isPromoting}
        onClick={promoteSelected}
        data-testid="staged-package-promotion-submit"
        style={{alignSelf: 'flex-start'}}
      >
        {isPromoting ? 'Promoting...' : `Promote Selected (${selectedCandidates.length})`}
      </button>
    </div>
  );
}
